import { createSlice } from "@reduxjs/toolkit"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { Library } from "../api/models"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import { FetchableState, useFetchableState } from "./fetchableState"
import { libraryThunk, transferEntitlementThunk } from "./libraryThunk"
import { switchSubscriptionThunk } from "./subscriptionSlice"
import { redeemCodeThunk, redeemResellerCodeThunk } from "./redemptionsSlice"
import { revokeActivationThunk } from "./activationsSlice"
import { claimRewardThunk } from "./storeSlice"

// The library is one unpaginated payload (entries + skus + products +
// licenses + activations) held whole: the page renders it as-is, so there is
// nothing to normalize into per-entity slices here. Anything that changes
// what the user owns or how their licenses look invalidates it — the data is
// kept so the page doesn't blank, and useLibrary refetches on the next
// render (or mount) once lastFetched is gone.

interface LibraryState {
    library: FetchableState<Library>
    debug: string
}

const initialState: LibraryState = {
    library: {
        data: undefined,
        currentRequestId: undefined,
        lastFetched: undefined
    },
    debug: "library state"
}

function invalidate(state: LibraryState) {
    state.library.lastFetched = undefined
    state.library.currentRequestId = undefined
}

export const librarySlice = createSlice({
    name: 'library',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(libraryThunk.pending, (state, action) => {
            if (state.library.currentRequestId)
                return
            state.library.currentRequestId = action.meta.requestId
        })
        builder.addCase(libraryThunk.fulfilled, (state, action) => {
            if (state.library.currentRequestId !== action.meta.requestId)
                return
            state.library.currentRequestId = undefined
            state.library.data = action.payload
            state.library.lastFetched = Date.now()
            state.library.error = undefined
        })
        builder.addCase(libraryThunk.rejected, (state, action) => {
            if (state.library.currentRequestId !== action.meta.requestId)
                return
            state.library.currentRequestId = undefined
            state.library.error = action.error
        })
        builder.addCase(switchSubscriptionThunk.fulfilled, invalidate)
        builder.addCase(redeemCodeThunk.fulfilled, invalidate)
        builder.addCase(redeemResellerCodeThunk.fulfilled, invalidate)
        builder.addCase(claimRewardThunk.fulfilled, invalidate)
        builder.addCase(revokeActivationThunk.fulfilled, invalidate)
        builder.addCase(transferEntitlementThunk.fulfilled, invalidate)
        builder.addCase(logOut, () => initialState)
    },
})

const librarySelector = (state: RootState) => state.library.library

export function useLibrary(accessToken: string) {
    const result = useFetchableState<Library>({
        selector: librarySelector,
        action: libraryThunk({ accessToken })
    })
    // an invalidation while the page is mounted (a code redeemed from the
    // global modal, an activation revoked) clears lastFetched — refetch
    // right away instead of waiting for the next mount
    const lastFetched = useSelector((state: RootState) => state.library.library.lastFetched)
    const stale = lastFetched === undefined && result.data !== undefined
    const { refresh } = result
    useEffect(() => {
        if (stale)
            refresh()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stale])
    return result
}

export default librarySlice.reducer
